import { observable, action, computed, makeObservable, runInAction, toJS } from 'mobx'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import GoldenRetriever from '@uppy/golden-retriever'
import React from 'react'

import api from '../service/api'

const PART_SIZE = 16 * 1024 * 1024
const JOBS_KEY = "hybrid-class-video-upload-jobs"
const RESTORE_TIMEOUT = 3000 // GoldenRetriever never emits "restored" when there is nothing to restore
const COMPLETION_TIMEOUT = 30000

const LIMITS = {
    source: { maxSize: 10 * 1024 ** 3, label: "Video" },
    thumbnail: { maxSize: 10 * 1024 ** 2, types: ["image/jpeg", "image/png", "image/webp"], label: "Thumbnail" },
}

const roleOf = ( file ) => {
    if ( file.meta?.role ) return file.meta.role
    if ( /^video\//.test( file.type || "" ) ) return "source"
    if ( LIMITS.thumbnail.types.includes( file.type ) ) return "thumbnail"
    return null
}

// A "ghost" is a file GoldenRetriever restored after reload WITHOUT its blob
// (IndexedDB only keeps files <= 10 MB by default). It cannot be uploaded until
// the user re-selects the same file from disk.
const isGhost = ( file ) => !file.isRemote && !file.progress?.uploadComplete && ( file.isGhost || file.data == null )

const missingInfo = ( file ) => ( { role: roleOf( file ), name: file.name, size: file.size } )

// Small IndexedDB store that keeps thumbnail blobs across reloads (independent of
// GoldenRetriever's store and its 300 MB total limit). Keyed by Uppy file id.
const THUMB_DB = "hybrid-class-video-upload-thumbs"
const THUMB_STORE = "thumbs"
const THUMB_TTL = 24 * 60 * 60 * 1000

const idbRequest = ( req ) => new Promise( ( resolve, reject ) => {
    req.onsuccess = () => resolve( req.result )
    req.onerror = () => reject( req.error )
} )

let thumbDbPromise = null
const openThumbDb = () => {
    if ( !thumbDbPromise ) {
        thumbDbPromise = new Promise( ( resolve, reject ) => {
            if ( typeof indexedDB === "undefined" ) return reject( new Error( "IndexedDB unavailable" ) )
            const req = indexedDB.open( THUMB_DB, 1 )
            req.onupgradeneeded = () => req.result.createObjectStore( THUMB_STORE, { keyPath: "id" } )
            req.onsuccess = () => resolve( req.result )
            req.onerror = () => reject( req.error )
        } )
        thumbDbPromise.catch( () => { thumbDbPromise = null } )
    }
    return thumbDbPromise
}

const thumbStore = {
    async run ( mode, fn ) {
        try {
            const db = await openThumbDb()
            return await idbRequest( fn( db.transaction( THUMB_STORE, mode ).objectStore( THUMB_STORE ) ) )
        } catch {
            return null // storage is best-effort
        }
    },
    // IndexedDB clones File objects with name/type/lastModified intact, so the restored
    // blob produces the same Uppy file id as the original.
    put: ( id, data ) => thumbStore.run( "readwrite", s => s.put( { id, data, expires: Date.now() + THUMB_TTL } ) ),
    async get ( id ) {
        const row = await thumbStore.run( "readonly", s => s.get( id ) )
        return row && row.expires > Date.now() ? row.data : null
    },
    delete: ( id ) => thumbStore.run( "readwrite", s => s.delete( id ) ),
    async cleanup () {
        const rows = await thumbStore.run( "readonly", s => s.getAll() )
        const now = Date.now()
        await Promise.all( ( rows || [] ).filter( r => r.expires <= now ).map( r => thumbStore.delete( r.id ) ) )
    },
}

const extractErrorMessage = ( err ) => err?.response?.data?.message || err?.message || "Something went wrong."

const jobsStore = {
    get () {
        try { return JSON.parse( localStorage.getItem( JOBS_KEY ) ) } catch { return null }
    },
    set ( jobs ) {
        try {
            if ( jobs.length ) localStorage.setItem( JOBS_KEY, JSON.stringify( jobs ) )
            else localStorage.removeItem( JOBS_KEY )
        } catch { /* storage unavailable */ }
    },
}

class HybridClassVideoUploadStore {
    jobs
    error
    trayMinimized
    lastSavedAt

    uppy = null
    discardedVideoIds = new Set()
    completionWatchdogs = new Map()
    finalizingJobs = new Set() // Guard against duplicate finalize calls
    liveFileIds = new Set() // Files that have an S3Uploader alive in THIS page session
    restored = Promise.resolve() // Resolves once GoldenRetriever has put files back into Uppy

    constructor () {
        const savedJobs = jobsStore.get() || []
        this.jobs = savedJobs.map( job => ( {
            ...job,
            status: ( job.status === "uploading" || job.status === "saving" )
                ? "failed"
                : job.status,
            error: ( job.status === "uploading" || job.status === "saving" )
                ? ( job.error || "Upload interrupted by page reload. Click resume to continue." )
                : job.error,
            needsFile: false,
            missingFiles: [],
            files: []
        } ) )


        this.error = null
        this.trayMinimized = false
        this.lastSavedAt = false

        makeObservable( this, {
            jobs: observable,
            error: observable,
            trayMinimized: observable,
            lastSavedAt: observable,
            getJobs: computed,
            getError: computed,
            hasActiveUpload: computed,
            patchJobMeta: action,
            patchJobFiles: action,
            removeJob: action,
            stopJob: action,
            resumeJob: action,
            setError: action,
            toggleTray: action
        } )

        this.initUppy()
        this.initNetworkListeners()
        thumbStore.cleanup()
    }

    get getJobs () {
        return toJS( this.jobs )
    }

    get getError () {
        return this.error
    }

    get hasActiveUpload () {
        return this.jobs.length > 0
    }

    getJobFiles ( jobId ) {
        return this.uppy.getFiles().filter( f => f.meta?.jobId === jobId )
    }

    patchJobMeta ( jobId, patch ) {
        runInAction( () => {
            this.jobs = this.jobs.map( job => job.id === jobId ? { ...job, ...patch } : job )
        } )
        this.persistJobs()
    }

    patchJobFiles ( jobId, updater ) {
        runInAction( () => {
            this.jobs = this.jobs.map( job => job.id === jobId ? { ...job, files: updater( job.files ) } : job )
        } )
    }

    removeJob ( jobId ) {
        runInAction( () => { this.jobs = this.jobs.filter( job => job.id !== jobId ) } )
        this.persistJobs()
    }

    stopJob ( jobId ) {
        const job = this.jobs.find( j => j.id === jobId )
        if ( !job || job.status !== "uploading" ) return

        // NOTE: Uppy has no pauseUpload()/resumeUpload(). The old calls threw and were
        // silently swallowed, so "stop" never actually stopped anything.
        this.getJobFiles( jobId ).forEach( f => {
            this.clearWatchdog( f.id )
            if ( f.isPaused || f.progress?.uploadComplete ) return
            if ( this.liveFileIds.has( f.id ) ) {
                this.uppy.pauseResume( f.id ) // toggles to paused -> S3Uploader aborts in-flight requests
            }
        } )

        this.patchJobFiles( jobId, files => files.map( f => f.state === "uploaded" ? f : { ...f, state: "paused" } ) )
        this.patchJobMeta( jobId, { status: "paused", error: "Upload stopped by user." } )
    }

    /**
     * Re-attach the original File objects after a page reload.
     * `reselected` = { video?: File, thumbnail?: File } picked by the user via <input type="file">.
     */
    reattachFiles ( jobId, { video, thumbnail } = {} ) {
        const ghosts = this.getJobFiles( jobId ).filter( isGhost )

        ghosts.forEach( ghost => {
            const picked = roleOf( ghost ) === "source" ? video : thumbnail
            if ( !picked ) return

            if ( picked.name !== ghost.name || picked.size !== ghost.size ) {
                throw new Error( `Please select the same file that was being uploaded: "${ghost.name}".` )
            }

            // Same name/type/size/lastModified/relativePath => same Uppy id => Uppy swaps the
            // blob into the ghost and KEEPS its progress and s3Multipart { uploadId, key }.
            const newId = this.uppy.addFile( {
                name: picked.name,
                type: picked.type,
                data: picked,
                meta: { ...ghost.meta }
            } )

            if ( newId !== ghost.id ) {
                this.uppy.removeFile( newId )
                throw new Error( `"${picked.name}" looks modified since the upload started. Please remove the job and upload it again.` )
            }
        } )
    }

    // Used by the tray: one picked File -> routed to the missing video or thumbnail slot.
    reattachAndResumeJob ( jobId, file ) {
        const role = LIMITS.thumbnail.types.includes( file.type ) ? "thumbnail" : "source"
        return this.resumeJob( jobId, role === "thumbnail" ? { thumbnail: file } : { video: file } )
    }

    async resumeJob ( jobId, reselected ) {
        await this.restored

        const job = this.jobs.find( j => j.id === jobId )
        if ( !job ) return
        if ( job.status === "saving" || job.status === "saved" ) return

        if ( reselected ) {
            try {
                this.reattachFiles( jobId, reselected )
            } catch ( err ) {
                const stillMissing = this.getJobFiles( jobId ).filter( isGhost )
                this.patchJobMeta( jobId, { status: "failed", needsFile: stillMissing.length > 0, missingFiles: stillMissing.map( missingInfo ), error: extractErrorMessage( err ) } )
                return
            }
        }

        const jobFiles = this.getJobFiles( jobId )

        // 1. Everything already in S3 (uploadComplete is only set after CompleteMultipartUpload
        //    succeeded; percentage === 100 is NOT enough) -> only finalize.
        const allUploaded = jobFiles.length > 0 && jobFiles.every( f => f.progress?.uploadComplete )
        if ( job.videoId && ( allUploaded || jobFiles.length === 0 ) ) {
            try {
                const res = await this.finalizeJob( jobId )
                if ( res ) return
            } catch { /* fall through */ }
            if ( allUploaded ) return // nothing left to upload; finalizeJob already set the error
        }

        // 2. Offline
        if ( !navigator.onLine ) {
            this.patchJobMeta( jobId, { status: "failed", error: "Still offline. Please check your connection." } )
            return
        }

        // 3. Nothing restored at all
        if ( !jobFiles.length ) {
            this.patchJobMeta( jobId, { status: "failed", error: "File reference lost on reload. Please remove and re-add the file." } )
            return
        }

        // 4. Restored without blob -> user must re-select the file. Previously the upload was
        //    started anyway, S3Uploader threw "File data is missing" inside a swallowed promise,
        //    no sign request was made and the progress bar stayed frozen.
        const missing = jobFiles.filter( isGhost )
        if ( missing.length ) {
            this.patchJobMeta( jobId, {
                status: "failed",
                needsFile: true,
                missingFiles: missing.map( missingInfo ),
                error: `Please re-select ${missing.map( f => `"${f.name}"` ).join( ", " )} to continue the upload.`
            } )
            return
        }

        // 5. Resume. Already-uploaded parts are discovered via ListParts (signed) and skipped.
        this.patchJobMeta( jobId, { status: "uploading", error: null, needsFile: false, missingFiles: [] } )

        jobFiles.forEach( f => {
            this.clearWatchdog( f.id )
            if ( f.progress?.uploadComplete ) return

            this.patchJobFiles( jobId, files => files.map( jf => jf.id === f.id ? { ...jf, state: "uploading" } : jf ) )

            if ( this.liveFileIds.has( f.id ) ) {
                // Uploader exists in this page (stopped, or hung on a request).
                // pause -> resume aborts the stuck request and restarts: ListParts + sign again.
                if ( !f.isPaused ) this.uppy.pauseResume( f.id )
                this.uppy.pauseResume( f.id )
            } else {
                // No uploader (after reload or after an error). A fresh S3Uploader reads
                // file.s3Multipart and resumes the same multipart upload.
                this.uppy.retryUpload( f.id ).catch( err => {
                    this.patchJobMeta( jobId, { status: "failed", error: extractErrorMessage( err ) } )
                } )
            }
        } )
        // Do NOT call uppy.upload() here: it retries every errored file of EVERY job.
    }

    persistJobs () {
        jobsStore.set( this.jobs.map( ( job ) => {
            const { id, videoId, title, topics_covered, status, error, institute, subjects, recording_metadata, thumbnail_metadata, module_number, module_name } = job
            return ( { id, videoId, title, topics_covered, status, error, institute, subjects, recording_metadata, thumbnail_metadata, module_number, module_name } )
        } ) )
    }

    setError ( message ) {
        runInAction( () => { this.error = message } )
    }

    toggleTray () {
        runInAction( () => {
            this.trayMinimized = !this.trayMinimized
            this.jobs = this.jobs.filter( j => j.status !== "saved" )
            this.persistJobs()
        } )
    }

    handleExpiredJob ( videoId ) {
        const job = this.jobs.find( j => j.videoId === videoId )
        if ( !job || job.status === "expired" ) return

        this.getJobFiles( job.id ).forEach( f => this.uppy.removeFile( f.id ) )
        this.patchJobMeta( job.id, { status: "expired", error: "Your upload session expired. Please add the file again." } )
    }

    initNetworkListeners () {
        window.addEventListener( "offline", () => {
            runInAction( () => {
                this.jobs.forEach( job => {
                    if ( job.status === "uploading" || job.status === "saving" ) {
                        this.patchJobMeta( job.id, {
                            status: "failed",
                            error: "Network connection lost. Click resume when back online."
                        } )
                    }
                } )
            } )
        } )
    }

    clearWatchdog ( fileId ) {
        if ( this.completionWatchdogs.has( fileId ) ) {
            clearTimeout( this.completionWatchdogs.get( fileId ) )
            this.completionWatchdogs.delete( fileId )
        }
    }

    // Put saved thumbnail blobs back into ghost thumbnails so the user never has to pick them.
    async restoreThumbnailBlobs () {
        const ghosts = this.uppy.getFiles().filter( f => roleOf( f ) === "thumbnail" && isGhost( f ) )

        await Promise.all( ghosts.map( async ( ghost ) => {
            const data = await thumbStore.get( ghost.id )
            if ( !data ) return

            try {
                // Same data object => same Uppy id => Uppy swaps the blob into the ghost.
                const newId = this.uppy.addFile( {
                    name: ghost.name,
                    type: data.type || ghost.type,
                    data,
                    meta: { ...ghost.meta }
                } )
                if ( newId !== ghost.id ) this.uppy.removeFile( newId )
            } catch ( err ) {
                console.warn( "Could not restore thumbnail blob", err )
            }
        } ) )
    }

    toJobFile ( f ) {
        return {
            id: f.id,
            name: f.name,
            sizeBytes: f.size,
            role: roleOf( f ),
            progress: f.progress?.uploadComplete ? 100 : ( f.progress?.percentage ?? 0 ),
            state: f.progress?.uploadComplete ? "uploaded" : isGhost( f ) ? "failed" : "paused"
        }
    }

    // GoldenRetriever puts files back with setState (no "file-added"), so the jobs'
    // file lists stayed empty after reload. Rebuild them here.
    hydrateRestoredFiles () {
        const byJob = new Map()
        this.uppy.getFiles().forEach( f => {
            const jobId = f.meta?.jobId
            if ( !jobId ) return
            if ( !byJob.has( jobId ) ) byJob.set( jobId, [] )
            byJob.get( jobId ).push( f )
        } )

        byJob.forEach( ( files, jobId ) => {
            const job = this.jobs.find( j => j.id === jobId )
            if ( !job || job.status === "saved" || job.status === "expired" ) {
                files.forEach( f => this.uppy.removeFile( f.id ) ) // leftovers, nothing to resume
                return
            }

            this.patchJobFiles( jobId, () => files.map( f => this.toJobFile( f ) ) )

            const missing = files.filter( isGhost )
            if ( missing.length && job.status === "failed" ) {
                this.patchJobMeta( jobId, {
                    needsFile: true,
                    missingFiles: missing.map( missingInfo ),
                    error: `Upload interrupted by page reload. Re-select ${missing.map( f => `"${f.name}"` ).join( ", " )} to continue.`
                } )
            }
        } )
    }

    initUppy () {
        this.uppy = new Uppy( {
            id: "hybrid-class-video-upload",
            autoProceed: false,
            restrictions: {
                allowedFileTypes: ["video/*", ...LIMITS.thumbnail.types]
            },

            onBeforeFileAdded: ( file, files ) => {
                const role = roleOf( file )
                if ( !role ) {
                    this.uppy.info( "Only video files and JPG, PNG or WebP thumbnails are allowed.", "error", 5000 )
                    return false
                }

                const existingFile = Object.values( files ).find(
                    f => f.meta.jobId === file.meta.jobId && roleOf( f ) === role
                )

                if ( existingFile ) {
                    const isDifferentFile = existingFile.name !== file.name || existingFile.size !== file.size
                    if ( isDifferentFile ) {
                        this.uppy.info(
                            `A different ${LIMITS[role].label.toLowerCase()} was selected for this job. Upload aborted to prevent key corruption.`,
                            "error",
                            5000
                        )
                        return false
                    }
                    return true
                }

                if ( file.size > LIMITS[role].maxSize ) {
                    this.uppy.info( `${LIMITS[role].label} is too large.`, "error", 5000 )
                    return false
                }
                return true
            }
        } )

        this.uppy.addPreProcessor( async ( fileIDs ) => {
            const files = fileIDs.map( id => this.uppy.getFile( id ) )
            const jobIds = [...new Set( files.map( f => f.meta.jobId ) )]

            await Promise.all( jobIds.map( async ( jobId ) => {
                const jobFiles = files.filter( f => f.meta.jobId === jobId )
                if ( jobFiles.every( f => f.meta.objectKey ) ) return // Reuse key; skip recreate

                try {
                    const source = jobFiles.find( f => roleOf( f ) === "source" )
                    const thumbnail = jobFiles.find( f => roleOf( f ) === "thumbnail" )
                    const describe = ( f ) => f && { name: f.name, type: f.type, size: f.size }

                    if ( source ) {
                        const { data } = await api.onlineMeetings.createHybridClassVideoUpload( { video: describe( source ), thumbnail: describe( thumbnail ) } )

                        this.uppy.setFileMeta( source.id, { videoId: data.data.video_id, objectKey: data.data.uploads.source.key } )
                        if ( thumbnail ) this.uppy.setFileMeta( thumbnail.id, { videoId: data.data.video_id, objectKey: data.data.uploads.thumbnail.key } )

                        this.patchJobMeta( jobId, { videoId: data.data.video_id } )
                    } else {
                        throw new Error( "Missing source file or Duplicate Upload" )
                    }
                } catch ( err ) {
                    const message = extractErrorMessage( err )
                    this.patchJobMeta( jobId, { status: "failed", error: message } )
                    jobFiles.forEach( f => this.uppy.removeFile( f.id ) )
                    throw err
                }
            } ) )
        } )

        // Must be registered BEFORE GoldenRetriever is installed.
        this.restored = new Promise( resolve => {
            const timer = setTimeout( resolve, RESTORE_TIMEOUT )
            this.uppy.once( "restored", async () => {
                clearTimeout( timer )
                try {
                    await this.restoreThumbnailBlobs()
                    this.hydrateRestoredFiles()
                } finally {
                    resolve()
                }
            } )
        } )

        this.uppy
            .use( AwsS3, {
                shouldUseMultipart: ( file ) => roleOf( file ) === "source",
                getChunkSize: () => PART_SIZE,
                allowedMetaFields: false,
                generateObjectKey: ( file ) => file.meta.objectKey,
                signRequest: async ( job ) => {
                    const { method, key, uploadId, partNumber, contentType } = job
                    const videoId = key.split( "/" )[2]
                    try {
                        const { data } = await api.onlineMeetings.signHybridClassVideoUpload( { video_id: videoId, method, key, uploadId, partNumber, contentType } )
                        return { url: data.data.url }
                    } catch ( err ) {
                        if ( err?.response?.status === 401 && !this.discardedVideoIds.has( videoId ) ) this.handleExpiredJob( videoId )
                        throw err
                    }
                }
            } )
            .use( GoldenRetriever )

        // Track which files have a live uploader in this page session.
        this.uppy.on( "upload", ( uploadId, files ) => {
            const jobIds = new Set()
            files.forEach( f => {
                this.liveFileIds.add( f.id )
                if ( f.meta?.jobId ) jobIds.add( f.meta.jobId )
            } )

            // uppy.upload() also silently retries errored files of other jobs; reflect that in the UI.
            jobIds.forEach( jobId => {
                const job = this.jobs.find( j => j.id === jobId )
                const batch = files.filter( f => f.meta?.jobId === jobId )
                if ( job?.status === "failed" && !batch.some( isGhost ) ) {
                    this.patchJobMeta( jobId, { status: "uploading", error: null } )
                }
            } )
        } )

        this.uppy.on( "file-added", ( file ) => {
            if ( roleOf( file ) === "thumbnail" && file.data && !file.isGhost ) thumbStore.put( file.id, file.data )

            // Upsert: re-selecting a ghost file emits file-added for an id we already track.
            this.patchJobFiles( file.meta.jobId, files => {
                const entry = { ...this.toJobFile( this.uppy.getFile( file.id ) || file ), state: "queued" }
                return files.some( f => f.id === file.id )
                    ? files.map( f => f.id === file.id ? { ...entry, progress: f.progress } : f )
                    : [...files, entry]
            } )
        } )

        this.uppy.on( "file-removed", ( file ) => {
            if ( roleOf( file ) === "thumbnail" ) thumbStore.delete( file.id )
            this.liveFileIds.delete( file.id )
            this.clearWatchdog( file.id )
            this.patchJobFiles( file.meta.jobId, files => files.filter( f => f.id !== file.id ) )
        } )

        this.uppy.on( "upload-progress", ( file, progress ) => {
            // Pause pressed while the file was still in the preprocessor / queue: the S3Uploader
            // is created afterwards and ignores isPaused, so it would keep uploading.
            // Re-send the pause event now that the uploader exists and is listening.
            const current = this.uppy.getFile( file.id )
            const owner = this.jobs.find( j => j.id === file.meta.jobId )
            if ( current?.isPaused || owner?.status === "paused" ) {
                if ( !current?.isPaused ) this.uppy.setFileState( file.id, { isPaused: true } )
                this.uppy.emit( "upload-pause", current || file, true )
                return
            }

            const percent = progress.bytesTotal ? Math.round( progress.bytesUploaded / progress.bytesTotal * 100 ) : 0
            this.patchJobFiles( file.meta.jobId, files => files.map( f => f.id === file.id ? { ...f, progress: percent, state: "uploading" } : f ) )

            // 100% of bytes sent != upload done (CompleteMultipartUpload still pending).
            // If it never completes, abort the hung request so "resume" can restart cleanly.
            if ( percent === 100 && !this.completionWatchdogs.has( file.id ) ) {
                const timer = setTimeout( async () => {
                    this.completionWatchdogs.delete( file.id )
                    const current = this.uppy.getFile( file.id )
                    if ( !current || current.progress?.uploadComplete ) return

                    const jobId = file.meta.jobId
                    const job = this.jobs.find( j => j.id === jobId )
                    if ( !job || job.status !== "uploading" ) return

                    if ( this.liveFileIds.has( file.id ) && !current.isPaused ) this.uppy.pauseResume( file.id )
                    this.patchJobFiles( jobId, files => files.map( f => f.id === file.id ? { ...f, state: "failed" } : f ) )
                    this.patchJobMeta( jobId, { status: "failed", error: "Upload stalled while completing. Click resume to continue." } )
                }, COMPLETION_TIMEOUT )

                this.completionWatchdogs.set( file.id, timer )
            }
        } )

        this.uppy.on( "upload-error", ( file, err ) => {
            if ( !file?.id ) return
            this.liveFileIds.delete( file.id ) // uploader is dead after an error
            this.clearWatchdog( file.id )
            if ( file.meta?.jobId ) {
                this.patchJobFiles( file.meta.jobId, files => files.map( f => f.id === file.id ? { ...f, state: "failed" } : f ) )
                this.patchJobMeta( file.meta.jobId, { status: "failed", error: extractErrorMessage( err ) } )
            }
        } )

        // Finalize per job on upload-success. The old "complete" listener never fired for
        // uploads started with retryUpload(), so resumed jobs stayed at 100% forever.
        this.uppy.on( "upload-success", ( file ) => {
            if ( !file?.id ) return
            this.liveFileIds.delete( file.id )
            this.clearWatchdog( file.id )

            if ( roleOf( file ) === "thumbnail" ) thumbStore.delete( file.id ) // in S3 now, no longer needed

            const jobId = file.meta?.jobId
            if ( !jobId ) return
            this.patchJobFiles( jobId, fs => fs.map( f => f.id === file.id ? { ...f, state: "uploaded", progress: 100 } : f ) )

            const jobFiles = this.getJobFiles( jobId )
            if ( jobFiles.length && jobFiles.every( f => f.progress?.uploadComplete ) ) {
                this.finalizeJob( jobId ).catch( () => { /* status already set in finalizeJob */ } )
            }
        } )
    }

    async addJob ( job ) {
        const { id, video, thumbnail, title, topics_covered = null, institute, subjects, recording_metadata = {}, thumbnail_metadata = {}, module_number, module_name } = job
        runInAction( () => {
            this.jobs = [...this.jobs, { id, videoId: null, title, topics_covered, status: "uploading", error: null, needsFile: false, missingFiles: [], files: [], institute, subjects, recording_metadata, thumbnail_metadata, module_number, module_name }]
        } )
        this.persistJobs()
        try {
            this.uppy.addFile( {
                name: video.name,
                type: video.type,
                data: video,
                meta: { jobId: id, role: "source", relativePath: `${id}_source` }
            } )

            if ( thumbnail ) {
                this.uppy.addFile( {
                    name: thumbnail.name,
                    type: thumbnail.type,
                    data: thumbnail,
                    meta: { jobId: id, role: "thumbnail", relativePath: `${id}_thumb` }
                } )
            }

        } catch ( err ) {
            this.patchJobMeta( id, { status: "failed", error: extractErrorMessage( err ) } )
            return id
        }

        this.uppy.upload().catch( err => {
            this.patchJobMeta( id, { status: "failed", error: extractErrorMessage( err ) } )
        } )

        return id
    }

    addJobs ( entries ) {
        runInAction( () => {
            this.trayMinimized = false
        } )
        return entries.map( entry => this.addJob( {
            id: entry.id,
            video: entry.file,
            thumbnail: entry.thumbnail || null,
            title: entry.name,
            institute: entry.institute,
            subjects: entry.subjects,
            topics_covered: entry.topics_covered || null,
            thumbnail_metadata: entry.thumbnail_metadata,
            recording_metadata: entry.recording_metadata,
            module_number: entry.module_number,
            module_name: entry.module_name,
        } ) )

    }

    async finalizeJob ( jobId ) {
        const job = this.jobs.find( j => j.id === jobId )

        if ( !job || !job.videoId ) return
        if ( job.status === "saving" || job.status === "saved" || this.finalizingJobs.has( jobId ) ) return

        this.finalizingJobs.add( jobId )
        this.patchJobMeta( jobId, { status: "saving" } )

        try {
            const { data } = await api.onlineMeetings.finalizeHybridClassVideoUpload( { recording_metadata: job.recording_metadata, thumbnail_metadata: job.thumbnail_metadata, video_id: job.videoId, title: job.title, topics_covered: job.topics_covered?.split( "," ).map( t => t.trim() ), institute: job.institute?.institute_name_short, subjects: job.subjects, module_number: job.module_number, module_name: job.module_name } )
            this.patchJobMeta( jobId, { status: "saved", error: null, needsFile: false, missingFiles: [] } )
            runInAction( () => {
                this.lastSavedAt = Date.now()
            } )
            return data.data
        } catch ( err ) {
            if ( err?.response?.status === 401 )
                this.patchJobMeta( jobId, { status: "expired", error: "Your upload session expired before the video was saved. Please upload it again." } )
            else {
                this.patchJobMeta( jobId, { status: "failed", error: extractErrorMessage( err ) } )
                throw err
            }
        } finally {
            this.finalizingJobs.delete( jobId )
        }
    }

    async discard ( jobId ) {
        const job = this.jobs.find( j => j.id === jobId )
        if ( !job ) return

        if ( job.videoId ) this.discardedVideoIds.add( job.videoId )
        this.getJobFiles( jobId ).forEach( f => this.uppy.removeFile( f.id ) )
        this.removeJob( jobId )

        if ( job.videoId ) {
            try {
                await api.onlineMeetings.discardHybridClassVideoUpload( { video_id: job.videoId } )
            } catch ( err ) {
                if ( err?.response?.status !== 401 ) throw err
            }
        }
    }
}

const HybridClassVideoUploadStoreInstance = new HybridClassVideoUploadStore()

export default HybridClassVideoUploadStoreInstance

const HybridClassVideoUploadStoreContext = React.createContext()

export const HybridClassVideoUploadStoreProvider = ( { children } ) => {
    return (
        <HybridClassVideoUploadStoreContext.Provider value={HybridClassVideoUploadStoreInstance}>{children}</HybridClassVideoUploadStoreContext.Provider>
    )
}

export const useHybridClassVideoUploadStore = () => React.useContext( HybridClassVideoUploadStoreContext )